import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import AgeVerification from '@/components/feature/AgeVerification';

const aboutImageUrl = 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20editorial%20office%20with%20a%20large%20bookshelf%20filled%20with%20art%20books%20and%20manga%20volumes%2C%20warm%20ambient%20lighting%2C%20modern%20creative%20workspace%2C%20professional%20yet%20artistic%20atmosphere%2C%20sophisticated%20dark%20wood%20and%20leather%20textures%2C%20fine%20art%20interior%20photography&width=1200&height=600&seq=about-manga-01&orientation=landscape';

export default function AboutPage() {
  const orgSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '成人漫画精选推荐',
    url: 'https://example.com',
    description: '专业的成人漫画内容导航与推荐平台',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@example.com',
      availableLanguage: 'Chinese',
    },
  }), []);

  return (
    <>
      <AgeVerification />
      <Navbar />

      <main className="pt-20">
        <section className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mt-4 mb-2">
              关于我们
            </h1>
            <p className="text-sm text-foreground-500">
              了解我们的品牌理念、团队背景与服务承诺
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-10">
              <img
                src={aboutImageUrl}
                alt="关于我们 - 成人漫画精选推荐团队"
                title="成人漫画精选推荐 - 专业编辑团队"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
              <div className="lg:col-span-2">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">成人漫画精选推荐</strong>成立于2024年，是一个专注于成人漫画内容导航与推荐的垂直平台。我们致力于为18岁以上的成熟读者提供专业、合规、高质量的漫画阅读指引。
                  </p>
                  <p>
                    在成人漫画这一广阔且多元的创作领域中，读者常常面临信息过载和选择困难。我们的团队由资深漫画编辑、文化研究者以及数字内容专家组成，通过严格的筛选机制和专业的评测体系，为读者提炼出最值得关注的优质作品和合规平台。
                  </p>
                  <p>
                    我们的核心价值在于<strong className="text-foreground-900">专业性、合规性和多样性</strong>。每篇推荐文章都经过多轮审校，确保内容准确、观点客观、分级明确。我们坚决拥护正版阅读，所有平台推荐均指向获得合法内容授权的正规渠道。
                  </p>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg border border-background-200/60 p-6">
                <h3 className="text-base font-bold font-heading text-foreground-950 mb-4">平台信息</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-calendar-check-line text-accent-500"></i>
                    </span>
                    <span className="text-foreground-600">成立时间：2024年</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-team-line text-accent-500"></i>
                    </span>
                    <span className="text-foreground-600">专业编辑团队</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-accent-500"></i>
                    </span>
                    <span className="text-foreground-600">严格内容分级审核</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-global-line text-accent-500"></i>
                    </span>
                    <span className="text-foreground-600">覆盖八大赛道</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 text-center">
                行业声誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="bg-background-100 rounded-lg border border-background-200/60 p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-500/10">
                    <i className="ri-article-line text-accent-500 text-xl"></i>
                  </div>
                  <div className="text-2xl font-bold font-heading text-foreground-950 mb-1">220+</div>
                  <p className="text-xs text-foreground-500">深度文章</p>
                </div>
                <div className="bg-background-100 rounded-lg border border-background-200/60 p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-500/10">
                    <i className="ri-group-line text-accent-500 text-xl"></i>
                  </div>
                  <div className="text-2xl font-bold font-heading text-foreground-950 mb-1">50万+</div>
                  <p className="text-xs text-foreground-500">月活跃读者</p>
                </div>
                <div className="bg-background-100 rounded-lg border border-background-200/60 p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-500/10">
                    <i className="ri-thumb-up-line text-accent-500 text-xl"></i>
                  </div>
                  <div className="text-2xl font-bold font-heading text-foreground-950 mb-1">98%</div>
                  <p className="text-xs text-foreground-500">读者好评率</p>
                </div>
                <div className="bg-background-100 rounded-lg border border-background-200/60 p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-500/10">
                    <i className="ri-shield-star-line text-accent-500 text-xl"></i>
                  </div>
                  <div className="text-2xl font-bold font-heading text-foreground-950 mb-1">0事故</div>
                  <p className="text-xs text-foreground-500">合规运营记录</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 text-center">
                联系我们
              </h2>
              <div className="max-w-lg mx-auto bg-background-100 rounded-lg border border-background-200/60 p-8">
                <form
                  id="contact-form-about"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d9q7n24nlsngrm4llsv0"
                  method="POST"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formEl = e.currentTarget;
                    const honeypot = (formEl.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                    if (honeypot) {
                      alert('感谢您的留言，我们会尽快回复！');
                      formEl.reset();
                      return;
                    }
                    const formData = new FormData(formEl);
                    const name = formData.get('name') as string;
                    const email = formData.get('email') as string;
                    const message = formData.get('message') as string;
                    if (!name || !email || !message) {
                      alert('请填写所有必填字段');
                      return;
                    }
                    if (message.length > 500) {
                      alert('留言内容不能超过500个字符');
                      return;
                    }
                    alert('感谢您的留言，我们会尽快回复！');
                    formEl.reset();
                  }}
                  className="space-y-5"
                >
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="about-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="about-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500个字符）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-3 rounded-md bg-accent-500 text-background-50 font-medium text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    提交留言
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <Footer />
    </>
  );
}